// Who is signed in, which company, which role, which modules. One hook for the whole app.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import { LEGACY_COMPANY_ID, paths, withDefaults, ENTITLEMENTS_DOC, type Entitlements } from '@tradeview/core';
import type { Role } from '@tradeview/shared';
import { CORE_MODULE_IDS } from './modules.generated';
import { resumePush } from './push';
import { setErrorReporter } from './errors';

export interface Profile { name: string; firstName?: string; lastName?: string; status?: string; email?: string }
export interface Session {
  user: User;
  companyId: string;
  role: Role | null;
  displayName: string;
  profile: Profile;
  entitlements: Entitlements;
  /** Firestore paths for this company (legacy = v1 root collections). */
  paths: ReturnType<typeof paths>;
}

interface AuthState { loading: boolean; session: Session | null; error: string | null }
const Ctx = createContext<AuthState>({ loading: true, session: null, error: null });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ loading: true, session: null, error: null });

  const load = useCallback(async (user: User) => {
    // Claims carry companyId/role once the tenancy function stamps them (later in Phase 1).
    // Until then, v1's users/{uid} document is where the role lives.
    const token = await user.getIdTokenResult();
    const companyId = (token.claims.companyId as string | undefined) ?? LEGACY_COMPANY_ID;
    const p = paths(companyId);
    const snap = await getDoc(doc(db, `${p.users}/${user.uid}`));
    const d = (snap.data() ?? {}) as Partial<Profile> & { role?: Role };
    const role = (token.claims.role as Role | undefined) ?? d.role ?? null;
    const displayName = d.name || [d.firstName, d.lastName].filter(Boolean).join(' ') || user.displayName || user.email || 'You';
    return { user, companyId, role, displayName, profile: { ...d, name: displayName }, paths: p };
  }, []);

  useEffect(() => {
    let stopEnt: (() => void) | null = null;
    const stopAuth = onAuthStateChanged(auth, async (user) => {
      stopEnt?.(); stopEnt = null;
      if (!user) { setErrorReporter(null); setState({ loading: false, session: null, error: null }); return; }
      try {
        const base = await load(user);
        setErrorReporter({ uid: user.uid, name: base.displayName, usersPath: base.paths.users });
        const entRef = doc(db, `${base.paths.settings}/${ENTITLEMENTS_DOC}`);
        // Live: when the office turns a module on, every phone sees it without a reload.
        stopEnt = onSnapshot(entRef, (s) => {
          const entitlements = withDefaults(s.exists() ? (s.data() as Entitlements) : null, CORE_MODULE_IDS);
          setState({ loading: false, error: null, session: { ...base, entitlements } });
        }, (e) => setState({ loading: false, session: { ...base, entitlements: withDefaults(null, CORE_MODULE_IDS) }, error: e.message }));
        void resumePush(user.uid, base.paths.users);
      } catch (e) {
        setState({ loading: false, session: null, error: e instanceof Error ? e.message : String(e) });
      }
    });
    return () => { stopAuth(); stopEnt?.(); };
  }, [load]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
